import { useDeferredValue, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { fromMinor, money, moneyShort, toMinor } from "@/lib/format";
import { useSession } from "@/lib/session";
import { GROUP_LABEL, atLeast, type Group, type ShopCustomer } from "@/lib/types";
import { Badge, Button, Field, Input, Modal, Pager, Select, cx, usePaged, useToast } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";

const GROUPS: Group[] = ["retail", "wholesale", "distributor", "staff"];
const GROUP_TONE: Record<Group, "slate" | "brand" | "violet" | "blue"> = { retail: "slate", wholesale: "brand", distributor: "violet", staff: "blue" };
const MAX_ROWS = 300;

/** Shop customers: their group sets which prices they get at the till, and any on-account limit. */
export function Customers() {
  const s = useSession();
  const manager = atLeast(s.staff?.role, "manager");
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<Group | "">("");
  const [edit, setEdit] = useState<ShopCustomer | null>(null);
  const [paying, setPaying] = useState<ShopCustomer | null>(null);
  const [adding, setAdding] = useState(false);
  const find = useDeferredValue(q.trim());
  const list = useQuery({
    queryKey: ["admin-customers", find, group],
    queryFn: () => api<{ customers: ShopCustomer[]; groups: Record<string, number> }>(till(`/admin/customers?q=${encodeURIComponent(find)}&group=${group}`)),
    placeholderData: keepPreviousData,
  });
  const counts = list.data?.groups ?? {};
  const rows = list.data?.customers ?? [];
  const paged = usePaged(rows);
  const total = Object.values(counts).reduce((n, v) => n + v, 0);
  const owed = rows.reduce((n, c) => n + Math.max(0, c.balance ?? 0), 0);
  const owing = rows.filter((c) => (c.balance ?? 0) > 0).length;
  const onCredit = rows.filter((c) => c.creditLimit).length;
  return (
    <>
      <PageHead title="Customers & groups" sub="Customers join this list when they give a phone number at the till. Their group sets the prices they pay.">
        {manager ? <Button tone="primary" onClick={() => setAdding(true)}>Add customer</Button> : null}
      </PageHead>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {([["Customers", rows.length.toLocaleString("en-GB"), group || find ? `Of ${total.toLocaleString("en-GB")} in total` : "On the list"], ["Can buy on credit", onCredit.toLocaleString("en-GB"), "Have a limit set"], ["Owed to you", moneyShort(owed), owing ? `By ${owing} customer${owing === 1 ? "" : "s"}` : "Nobody owes right now"]] as const).map(([l, v, n]) => (
            <div key={l} className="min-w-0 border border-slate-200 bg-white p-4">
              <span className="text-xs font-semibold text-slate-500">{l}</span>
              <div className="mt-1 truncate font-mono text-2xl font-bold">{v}</div>
              <div className="mt-0.5 text-xs text-slate-500">{n}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" tone={group === "" ? "dark" : "light"} onClick={() => setGroup("")}>All · <span className="font-mono">{total}</span></Button>
          {GROUPS.map((g) => (
            <Button key={g} size="sm" tone={group === g ? "dark" : "light"} onClick={() => setGroup(g)}>
              {GROUP_LABEL[g]} · <span className="font-mono">{counts[g] ?? 0}</span>
            </Button>
          ))}
          <Input aria-label="Search customers" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone" className="!h-10 w-full sm:ml-auto sm:!w-64" />
        </div>
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={th}>Customer</th>
                  <th className={th}>Phone</th>
                  <th className={th}>Group</th>
                  <th className={th}>Can owe</th>
                  <th className={cx(th, "text-right")}>Owes now</th>
                  <th className={th}><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((c) => (
                  <tr key={c.id}>
                    <td className={cx(td, "max-w-[340px]")}><b className="block truncate">{c.name || "No name"}</b>{c.email || c.note ? <span className="block truncate text-xs text-slate-500">{[c.note, c.email].filter(Boolean).join(" · ")}</span> : null}</td>
                    <td className={cx(td, "font-mono")}>{c.phone}</td>
                    <td className={td}><Badge tone={GROUP_TONE[c.group]}>{GROUP_LABEL[c.group]}</Badge></td>
                    <td className={td}>{c.creditLimit ? <span className="font-mono">Up to {moneyShort(c.creditLimit)}</span> : <span className="text-slate-500">Pays in full</span>}</td>
                    <td className={cx(td, "text-right font-mono", (c.balance ?? 0) > 0 ? "font-bold" : "text-slate-500")}>{(c.balance ?? 0) > 0 ? money(c.balance!) : "Nothing"}</td>
                    <td className={td}>
                      <span className="flex justify-end gap-1.5">
                        {(c.balance ?? 0) > 0 ? <Button size="sm" tone="primary" onClick={() => setPaying(c)}>Take payment</Button> : null}
                        {manager ? <Button size="sm" aria-label={`Edit ${c.name || c.phone}`} onClick={() => setEdit(c)}>Edit</Button> : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.isError ? <p className="px-5 py-6 text-center text-sm text-slate-500">{list.error instanceof Error ? list.error.message : "Couldn't load customers"}</p> : list.isLoading ? <p className="px-5 py-6 text-center text-sm text-slate-500">Loading…</p> : !rows.length ? <p className="px-5 py-8 text-center text-sm text-slate-500">{find || group ? "No customers match." : "No customers yet. They join when a teller adds a phone number to a sale, or use Add customer."}</p> : null}
            <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
            {rows.length >= MAX_ROWS ? <p className="px-5 py-3 text-center text-xs text-slate-500">Showing the latest {MAX_ROWS}. Search to find anyone else.</p> : null}
          </div>
        </Card>
      </div>
      {edit ? <EditCustomer c={edit} onClose={() => setEdit(null)} /> : null}
      {paying ? <TakePayment c={paying} onClose={() => setPaying(null)} /> : null}
      {adding ? <AddCustomer onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function AddCustomer({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState({ phone: "", name: "", group: "retail" as Group, credit: "", note: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api(till("/admin/customers"), { body: { phone: f.phone, name: f.name, group: f.group, creditLimit: f.credit === "" ? null : toMinor(f.credit), note: f.note } });
      toast("Customer added");
      void qc.invalidateQueries({ queryKey: ["admin-customers"] });
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't add them", "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Add a customer" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button tone="primary" disabled={busy || !f.phone.trim() || !f.name.trim()} onClick={() => void save()}>Add customer</Button></>}>
      <div className="grid gap-3">
        <Field label="Customer or company name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={80} autoFocus /></Field>
        <Field label="Phone number"><Input inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="font-mono" /></Field>
        <Field label="Group" hint="Sets the prices the till charges them">
          <Select value={f.group} onChange={(e) => setF({ ...f, group: e.target.value as Group })}>{GROUPS.map((g) => <option key={g} value={g}>{GROUP_LABEL[g]}</option>)}</Select>
        </Field>
        <Field label="Can owe up to" hint="Leave blank if they always pay in full"><Input inputMode="decimal" value={f.credit} onChange={(e) => setF({ ...f, credit: e.target.value })} className="font-mono" /></Field>
        <Field label="Note"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={300} /></Field>
      </div>
    </Modal>
  );
}

function EditCustomer({ c, onClose }: { c: ShopCustomer; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: c.name ?? "", email: c.email ?? "", group: c.group, credit: c.creditLimit != null ? fromMinor(c.creditLimit) : "", terms: String(c.termsDays ?? 30), note: c.note ?? "" });
  const [buyers, setBuyers] = useState((c.buyers ?? []).map((b) => ({ name: b.name, limit: b.limit != null ? fromMinor(b.limit) : "" })));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api(till(`/admin/customers/${c.id}`), {
        method: "PUT",
        body: {
          name: f.name, email: f.email, group: f.group, creditLimit: f.credit === "" ? null : toMinor(f.credit), note: f.note,
          termsDays: f.terms === "" ? null : Number(f.terms),
          buyers: buyers.filter((b) => b.name.trim()).map((b) => ({ name: b.name.trim(), limit: b.limit === "" ? null : toMinor(b.limit) })),
        },
      });
      toast("Saved");
      void qc.invalidateQueries({ queryKey: ["admin-customers"] });
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={c.name || c.phone} sub={c.phone} onClose={onClose} footer={<Button tone="primary" disabled={busy} onClick={() => void save()}>Save</Button>}>
      <div className="grid gap-3">
        <Field label="Group" hint="Sets the prices the till charges them">
          <Select value={f.group} onChange={(e) => setF({ ...f, group: e.target.value as Group })}>
            {GROUPS.map((g) => <option key={g} value={g}>{GROUP_LABEL[g]}</option>)}
          </Select>
        </Field>
        <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Can owe up to" hint="Leave blank if they always pay in full">
          <Input inputMode="decimal" value={f.credit} onChange={(e) => setF({ ...f, credit: e.target.value })} className="font-mono" />
        </Field>
        {f.credit !== "" ? (
          <>
            <Field label="Days to pay" hint="Shown as the due date at the till and on the account">
              <Input inputMode="numeric" value={f.terms} onChange={(e) => setF({ ...f, terms: e.target.value.replace(/\D/g, "") })} />
            </Field>
            <div className="flex flex-col gap-2">
              <span className="text-[13px] font-semibold">Who can buy on credit for them</span>
              <span className="text-xs text-slate-500">Leave empty to let anyone, with a supervisor's PIN. If you name people, only they can, each up to their own amount per sale.</span>
              {buyers.map((b, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                  <Input aria-label="Name" value={b.name} onChange={(e) => setBuyers(buyers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="e.g. Ato Quaye (site foreman)" className="!w-56" />
                  up to <Input inputMode="decimal" aria-label="Most per sale" value={b.limit} onChange={(e) => setBuyers(buyers.map((x, j) => (j === i ? { ...x, limit: e.target.value } : x)))} placeholder="any" className="!w-28 font-mono" /> a sale
                  <Button size="sm" onClick={() => setBuyers(buyers.filter((_, j) => j !== i))}>Remove</Button>
                </div>
              ))}
              {buyers.length < 12 ? <Button size="sm" className="self-start" onClick={() => setBuyers([...buyers, { name: "", limit: "" }])}>+ Add a person</Button> : null}
            </div>
          </>
        ) : null}
        <Field label="Note"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={300} /></Field>
      </div>
    </Modal>
  );
}

/** A customer pays toward their account. Cash goes into this till's drawer. */
function TakePayment({ c, onClose }: { c: ShopCustomer; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [amount, setAmount] = useState(fromMinor(c.balance ?? 0));
  const [method, setMethod] = useState("momo");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api<{ paid: number; balance: number }>(till(`/customers/${c.id}/payments`), { body: { amount: toMinor(amount), method, reference: ref } });
      toast(`Took ${money(r.paid)}. ${r.balance > 0 ? `Still owes ${money(r.balance)}` : "Nothing left to pay"}`);
      void qc.invalidateQueries({ queryKey: ["admin-customers"] });
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't record it", "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Take a payment from ${c.name || c.phone}`} sub={`Owes ${money(c.balance ?? 0)}. This pays off the oldest sale first.`} onClose={onClose} footer={<Button tone="primary" disabled={busy || !amount} onClick={() => void save()}>Record payment</Button>}>
      <div className="grid gap-3">
        <Field label="Amount"><Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono text-lg" autoFocus /></Field>
        <Field label="Paid by" hint="Cash is taken at a till, so it goes in that till's drawer">
          <Select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="momo">Mobile Money</option>
            <option value="card">Card</option>
          </Select>
        </Field>
        {method !== "cash" ? <Field label={method === "card" ? "Approval code" : "Mobile Money reference"}><Input value={ref} onChange={(e) => setRef(e.target.value)} className="font-mono" /></Field> : null}
      </div>
    </Modal>
  );
}
